/**
 * The printable card games (ADR 0135): each game's rules and the deck it is played with.
 *
 * This file is the one source for both the page and the PDF. The page lays the rules out as
 * HTML and the PDF prints them on its first page, from these same strings, so the rules a
 * child reads on the table are the rules the adult read on screen (ADR 0077).
 *
 * Every string here is printed with the PDF's standard fonts, so it must stay inside WinAnsi
 * (ADR 0070): a minus sign is the en dash "–", never U+2212, which prints as nothing.
 * `cardGames.test.ts` checks that, and checks every count and worked example against the deck.
 */

export type CardGameId = "makeTen" | "placeValue" | "productShowdown" | "fractionLineUp" | "zeroOut";

/** One printed card. Values are whole numbers; a fraction keeps its own numerator and denominator. */
export type CardFace =
    /** A numeral. `tenFrame` also draws it on a ten-frame (0 to 10 only). */
    | { kind: "number"; value: number; tenFrame?: boolean }
    /** A fraction with a bar split into `denominator` equal parts, `numerator` of them shaded. */
    | { kind: "fraction"; numerator: number; denominator: number }
    /** An end of the fraction line: "0" with an empty bar, or "1" with a full one. */
    | { kind: "fractionEnd"; value: 0 | 1 }
    /** A signed integer with its counters: filled for positive, open for negative, one zero pair for 0. */
    | { kind: "integer"; value: number };

/** A table each player writes on as they play. Two copies print on a page; the PDF prints `copies`. */
export interface RecordSheet {
    title: string;
    columns: string[];
    rows: number;
    /** A last "Total" row under the table. */
    totalRow?: boolean;
    copies: number;
}

export interface CardGame {
    id: CardGameId;
    name: string;
    grades: string;
    players: string;
    time: string;
    /** One sentence: how you win. */
    goal: string;
    youNeed: string[];
    setUp: string[];
    /** The steps of a turn or round, in order. */
    howToPlay: string[];
    /** A worked turn. Its arithmetic is checked in `cardGames.test.ts`. */
    example: string;
    winning: string;
    easier: string;
    harder: string;
    /** What the game practises: 2–3 verb phrases, as a page's objectives are. */
    practises: string[];
    deck: CardFace[];
    recordSheet?: RecordSheet;
}

function copies<T>(faces: T[], n: number): T[] {
    return Array.from({ length: n }, () => faces).flat();
}

function range(from: number, to: number): number[] {
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}

const numberCards = (values: number[], tenFrame = false): CardFace[] =>
    values.map((value) => (tenFrame ? { kind: "number", value, tenFrame } : { kind: "number", value }));

/** Halves, thirds, fourths, sixths and eighths below one: 18 fractions. */
const FRACTIONS: CardFace[] = [2, 3, 4, 6, 8].flatMap((denominator) =>
    range(1, denominator - 1).map((numerator): CardFace => ({ kind: "fraction", numerator, denominator }))
);

const makeTen: CardGame = {
    id: "makeTen",
    name: "Make Ten Catch",
    grades: "Grades K–1",
    players: "2–4 players",
    time: "10 minutes",
    goal: "Catch the most pairs of cards that make 10.",
    youNeed: [
        "The 40 cards from this PDF: four each of 1 to 9, two 0s and two 10s.",
    ],
    setUp: [
        "Shuffle the cards.",
        "Lay 8 cards face up in two rows of 4. This is the pond.",
        "Put the rest in a stack, face down.",
    ],
    howToPlay: [
        "On your turn, find two cards in the pond that make 10.",
        "Say the fact out loud, like \"6 and 4 make 10\", and keep the pair.",
        "Keep going while you can find pairs. Then fill the gaps in the pond from the stack.",
        "If you can't find a pair, turn one card from the stack into the pond. Your turn is over.",
    ],
    example:
        "The pond shows 3, 9, 6, 5, 1, 8, 4 and 2. Mia takes 6 and 4, then 9 and 1, then 8 and 2. " +
        "That leaves 3 and 5, which make 8, so she fills the pond and play passes on.",
    winning: "When the stack is empty and no two cards in the pond make 10, the player with the most pairs wins.",
    easier:
        "Use the ten-frames. Count the empty boxes on a card: that is the number its partner needs.",
    harder: "Three cards can make 10 too. Take any two or three cards that add up to 10, like 2, 3 and 5.",
    practises: [
        "Find the number that makes 10 with any number from 0 to 10",
        "Add within 10 quickly and say the fact",
    ],
    deck: [...copies(numberCards(range(1, 9), true), 4), ...copies(numberCards([0, 10], true), 2)],
};

const placeValue: CardGame = {
    id: "placeValue",
    name: "Biggest Number",
    grades: "Grade 2",
    players: "2–4 players",
    time: "15 minutes",
    goal: "Build the biggest three-digit number in the most rounds.",
    youNeed: [
        "The 40 digit cards from this PDF: four each of 0 to 9.",
        "A record sheet and a pencil for each player.",
    ],
    setUp: ["Shuffle the cards and put them in a stack, face down."],
    howToPlay: [
        "One player turns over the top card for everyone.",
        "Every player writes that digit in one empty box of this round's row: hundreds, tens or ones. " +
            "Once a digit is written, it stays.",
        "Turn over two more cards the same way, until every box in the row is full.",
        "Each player reads their number out loud. The biggest number wins the round: circle it.",
    ],
    example:
        "The cards are 2, then 9, then 4. Ava writes 2 in the ones, 9 in the hundreds and 4 in the tens: 942. " +
        "Ben puts 2 in the hundreds, then 9 in the tens and 4 in the ones: 294. 942 is bigger than 294, so Ava wins the round.",
    winning: "Play 6 rounds. The player who wins the most rounds wins. Shuffle the cards again if the stack runs out.",
    easier: "Make two-digit numbers. Leave the hundreds box empty and use tens and ones.",
    harder: "Play for the smallest number, or for the number closest to 500.",
    practises: [
        "Use place value to build three-digit numbers",
        "Read three-digit numbers and compare them",
    ],
    deck: copies(numberCards(range(0, 9)), 4),
    recordSheet: {
        title: "Biggest Number",
        columns: ["Round", "Hundreds", "Tens", "Ones", "Won?"],
        rows: 6,
        copies: 4,
    },
};

const productShowdown: CardGame = {
    id: "productShowdown",
    name: "Product Showdown",
    grades: "Grade 3",
    players: "2 players",
    time: "10–15 minutes",
    goal: "Win the most cards by making the bigger product.",
    youNeed: ["The 40 cards from this PDF: four each of 1 to 10."],
    setUp: ["Shuffle the cards and deal them all out, face down. Each player gets a stack of 20."],
    howToPlay: [
        "Both players turn over the top two cards of their stack at the same time.",
        "Multiply your two cards and say the fact out loud, like \"6 times 7 is 42\".",
        "The bigger product wins all four cards. Put them in your won pile.",
        "If the products are the same, it's a showdown: each player turns two more cards, and the bigger product wins all eight.",
    ],
    example:
        "You turn 6 and 7: 6 × 7 = 42. Your partner turns 9 and 4: 9 × 4 = 36. 42 is bigger than 36, so you win all four cards. " +
        "Next you turn 3 and 8, and your partner turns 4 and 6. Both make 24, so it's a showdown.",
    winning:
        "When both stacks are used up, count your won piles. The most cards wins. A showdown with no cards left to turn wins nothing.",
    easier: "Play Sum Showdown: add your two cards instead of multiplying.",
    harder: "Turn three cards. Multiply two of them and add the third, in whichever way makes the biggest number.",
    practises: [
        "Multiply within 100 with factors from 1 to 10",
        "Compare two products",
    ],
    deck: copies(numberCards(range(1, 10)), 4),
};

const fractionLineUp: CardGame = {
    id: "fractionLineUp",
    name: "Fraction Line-Up",
    grades: "Grades 3–4",
    players: "2–4 players",
    time: "15 minutes",
    goal: "Be the first to place all your fraction cards on the line in order.",
    youNeed: [
        "The 38 cards from this PDF: two each of 18 fractions, from 1/8 to 7/8, and the two end cards, 0 and 1.",
    ],
    setUp: [
        "Lay the 0 card on the left and the 1 card on the right, with room between them. This is the line.",
        "Shuffle the other cards. Deal 5 to each player and put the rest in a stack, face down.",
    ],
    howToPlay: [
        "On your turn, put one card on the line where it belongs: bigger than every card to its left " +
            "and smaller than every card to its right.",
        "A card equal to one already on the line goes on top of it. 2/4 goes on 1/2.",
        "If anyone thinks a card is in the wrong place, lay its fraction bar under the bars of its neighbours to check.",
        "A card in the wrong place goes back to its player, who also takes one card from the stack.",
    ],
    example:
        "The line shows 0, 1/4, 2/3 and 1. You play 1/2 between 1/4 and 2/3, because 1/4 is less than 1/2 " +
        "and 1/2 is less than 2/3. Your partner plays 3/6 on top of your 1/2, because they are equal.",
    winning: "The first player with no cards left wins.",
    easier: "Take out the thirds and sixths, and play with halves, fourths and eighths.",
    harder: "Before you place a card, say whether it is less than, equal to or more than 1/2.",
    practises: [
        "Compare fractions with different denominators",
        "Recognise equivalent fractions such as 1/2, 2/4, 3/6 and 4/8",
    ],
    deck: [...copies(FRACTIONS, 2), { kind: "fractionEnd", value: 0 }, { kind: "fractionEnd", value: 1 }],
};

const integerCards = (values: number[]): CardFace[] => values.map((value) => ({ kind: "integer", value }));

const zeroOut: CardGame = {
    id: "zeroOut",
    name: "Zero Out",
    grades: "Grade 7",
    players: "2–4 players",
    time: "15 minutes",
    goal: "Play cards that add up as close to zero as you can, and finish with the lowest score.",
    youNeed: [
        "The 40 cards from this PDF: two each of –9 to –1 and 1 to 9, and four 0s.",
        "A record sheet and a pencil for each player.",
    ],
    setUp: ["Shuffle the cards. Deal 6 to each player and put the rest in a stack, face down."],
    howToPlay: [
        "Everyone chooses 3 cards from their hand and lays them face down.",
        "Turn them over together. Add your three cards and write the sum on your record sheet.",
        "Your points for the round are how far your sum is from zero. A sum of –2 or 2 scores 2 points.",
        "Everyone takes 3 cards from the stack, back up to 6. When the stack runs out, shuffle the played cards to make a new one.",
    ],
    example:
        "You play 7, –4 and –5: 7 + (–4) + (–5) = –2, so you score 2 points. " +
        "Your partner plays –8, 6 and 3: –8 + 6 + 3 = 1, so they score 1 point.",
    winning: "Play 5 rounds and add up your points. The lowest total wins.",
    easier: "Play 2 cards each round instead of 3.",
    harder:
        "Play 2 cards each round and subtract the second from the first. 3 – (–5) = 8, so that scores 8 points.",
    practises: [
        "Add positive and negative integers",
        "Use absolute value as distance from zero",
    ],
    deck: [...copies(integerCards([...range(-9, -1), ...range(1, 9)]), 2), ...integerCards([0, 0, 0, 0])],
    recordSheet: {
        title: "Zero Out",
        columns: ["Round", "My cards", "Sum", "Points"],
        rows: 5,
        totalRow: true,
        copies: 4,
    },
};

export const CARD_GAMES: Record<CardGameId, CardGame> = {
    makeTen,
    placeValue,
    productShowdown,
    fractionLineUp,
    zeroOut,
};

export function cardGame(id: CardGameId): CardGame {
    return CARD_GAMES[id];
}
